import { formatTimestamp } from "@/features/annotation/lib/helpers";
import { displayUUID } from "@/shared";
import { Order, useStagesQuery } from "@/shared/api/lod-api";
import { CopyableText } from "@/shared/ui";
import { SortDirection, SourceOption } from "../lib/types";
import {
  Box,
  Button,
  DataList,
  Flex,
  IconButton,
  Spinner,
  Text,
  Theme,
  VisuallyHidden,
} from "@radix-ui/themes";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { useMemo, useRef, useState } from "react";
import { FilterSection } from "./filter-section";
import { useUrlQueries } from "@/shared/hooks/useUrlQueries";
import { ExportCollections } from "./export-collections";
import { useScrollState } from "../hooks/useScrollState";
import { StageStatusItem } from "./stage-status-item";
import { AnnotationStageDetailsDialog } from "./annotation-stage-statuses-dialog";
import { Drawer } from "vaul";
import { Cross2Icon } from "@radix-ui/react-icons";
import MdiFilterList from "~icons/mdi/filter-list";
import { useAnnotatorData } from "@/shared/hooks/useAnnotatorData";

interface OrdersProps {
  orders: Order[];
  onSelect: (order: Order) => void;
  highlightedOrderId: string | null;
  isLoading?: boolean;
  isFetchingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => Promise<void>;
  totalCount?: number;
  sourceList?: SourceOption[];
  filterState?: {
    orderId: string;
    source: string;
    fromDate: Date | null;
    toDate: Date | null;
    sortDirection: SortDirection;
    showFilters: boolean;
    isFiltersApplied: boolean;
    isInitialized: boolean;
    assignee: string;
    stageJourneyAnnotation: string;
    stageTransactionAnnotation: string;
    stageTrackPersistenceAnnotation: string;
  };
  filterHandlers?: {
    onOrderIdChange: (value: string) => void;
    onSourceChange: (value: string) => void;
    onFromDateChange: (date: Date | null) => void;
    onToDateChange: (date: Date | null) => void;
    onAssigneeChange: (value: string) => void;
    onSortDirectionChange: (value: SortDirection) => void;
    onStageJourneyAnnotationChange: (value: string) => void;
    onStageTransactionAnnotationChange: (value: string) => void;
    onStageTrackPersistenceAnnotationChange: (value: string) => void;
    toggleFilters: () => void;
    applyFilters: () => void;
    clearFilters: () => void;
  };
}

export function Orders({
  orders,
  onSelect,
  highlightedOrderId,
  isLoading = false,
  isFetchingMore = false,
  hasMore = true,
  onLoadMore,
  totalCount = 0,
  sourceList = [],
  filterState,
  filterHandlers,
}: OrdersProps) {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const orderRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [stageDetailsOrder, setStageDetailsOrder] = useState<Order | null>(
    null
  );

  const { saveScrollPosition } = useScrollState(virtuosoRef, {
    storageKey: "orders-scroll-position",
    isLoading,
    items: orders,
    highlightedItemId: highlightedOrderId,
    getItemId: (order) => order.id,
    isFetchingMore,
    resetTrigger: filterState?.isFiltersApplied,
    highlightScrollBehavior: "auto",
  });

  const { getAllQueries } = useUrlQueries();

  const {
    startDate,
    orderId: urlOrderId,
    source: urlSource,
    sortDirection: urlSortDirection,
  } = getAllQueries();

  const { annotatorOptions, annotatorLookup } = useAnnotatorData();

  const hasActiveFilters =
    startDate || urlOrderId || urlSource || urlSortDirection;

  const handleSelect = (order: Order) => {
    saveScrollPosition();
    onSelect(order);
  };

  const handleEndReached = () => {
    if (hasMore && !isFetchingMore && onLoadMore) {
      onLoadMore();
    }
  };

  const Footer = () => {
    if (isFetchingMore) {
      return (
        <Flex justify="center" align="center" p="4">
          <Spinner size="2" />
          <Text ml="2" size="2" color="gray">
            Loading more orders...
          </Text>
        </Flex>
      );
    }

    if (!hasMore && orders.length > 0) {
      return (
        <Flex justify="center" align="center" p="4">
          <Text size="2" color="gray">
            You've reached the end.
          </Text>
        </Flex>
      );
    }

    return null;
  };

  const { data: stages } = useStagesQuery();
  const stageStatusOptions = useMemo(
    () => stages?.result?.statuses || [],
    [stages]
  );

  return (
    <Box className="h-full">
      <Flex px="2" py="1" gap="2" align="center" className="border-b">
        <Flex align="baseline" gap="1">
          <Text weight="bold">Orders</Text>
          <Text size="1" color="gray">
            (Total: {totalCount})
          </Text>
        </Flex>
        <div className="grow" />
        {orders.length > 0 && <ExportCollections />}
        <IconButton
          size="1"
          variant="soft"
          color={hasActiveFilters ? "blue" : "gray"}
          onClick={() => filterHandlers?.toggleFilters()}
        >
          {filterState?.showFilters ? <Cross2Icon /> : <MdiFilterList />}
        </IconButton>
      </Flex>

      <div className="h-[calc(100%-2rem)] relative [&>[data-vaul-drawer]]:absolute [&>[data-vaul-drawer]]:z-20">
        <Drawer.Root
          open={filterState?.showFilters}
          direction="right"
          fixed={false}
          dismissible={false}
          disablePreventScroll={false}
        >
          <Drawer.Overlay className="absolute inset-0 bg-black/40 z-10" />
          <Drawer.Content
            className="h-[calc(100%-1.25rem)] absolute top-2 left-2 right-2 outline-none rounded-xl overflow-hidden"
            style={
              {
                "--initial-transform": "calc(100% + 0.5rem)",
              } as React.CSSProperties
            }
            onOpenAutoFocus={(event) => {
              // A hack to fix pointer events being disabled when the drawer is opened (https://github.com/radix-ui/primitives/issues/1241#issuecomment-2932189460)
              event.preventDefault();
              document.body.style.pointerEvents = "";
              document.body.removeAttribute("data-scroll-locked");
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              document.body.style.pointerEvents = "";
              document.body.removeAttribute("data-scroll-locked");
            }}
          >
            <VisuallyHidden>
              <Drawer.Title>Filter</Drawer.Title>
            </VisuallyHidden>
            <Theme className="h-full">
              {filterState && filterHandlers && (
                <FilterSection
                  fromDate={filterState.fromDate}
                  toDate={filterState.toDate}
                  assignee={filterState.assignee}
                  assigneeList={annotatorOptions}
                  onToDateChange={filterHandlers.onToDateChange}
                  onFromDateChange={filterHandlers.onFromDateChange}
                  onAssigneeChange={filterHandlers.onAssigneeChange}
                  isFiltersApplied={filterState.isFiltersApplied}
                  orderId={filterState.orderId}
                  onOrderIdChange={filterHandlers.onOrderIdChange}
                  sourceList={sourceList}
                  source={filterState.source}
                  onSourceChange={filterHandlers.onSourceChange}
                  sortDirection={filterState.sortDirection}
                  onSortDirectionChange={filterHandlers.onSortDirectionChange}
                  applyFilters={filterHandlers.applyFilters}
                  clearFilters={filterHandlers.clearFilters}
                  isLoading={isLoading}
                  totalCount={totalCount}
                  stageStatusOptions={stageStatusOptions}
                  stageJourneyAnnotation={filterState.stageJourneyAnnotation}
                  stageTransactionAnnotation={
                    filterState.stageTransactionAnnotation
                  }
                  stageTrackPersistenceAnnotation={
                    filterState.stageTrackPersistenceAnnotation
                  }
                  onStageJourneyAnnotationChange={
                    filterHandlers.onStageJourneyAnnotationChange
                  }
                  onStageTransactionAnnotationChange={
                    filterHandlers.onStageTransactionAnnotationChange
                  }
                  onStageTrackPersistenceAnnotationChange={
                    filterHandlers.onStageTrackPersistenceAnnotationChange
                  }
                />
              )}
            </Theme>
          </Drawer.Content>
        </Drawer.Root>

        {isLoading ? (
          <Flex justify="center" align="center" className={"h-20"}>
            <Spinner />
          </Flex>
        ) : orders.length === 0 ? (
          <Flex justify="center" align="center" className="h-[120px]">
            <Text size="1" color="gray" className="w-[200px]" align="center">
              No results found. <br /> Please review your filters <br /> or
              contact your administrator.
            </Text>
          </Flex>
        ) : (
          <Virtuoso
            ref={virtuosoRef}
            data={orders}
            endReached={handleEndReached}
            atBottomThreshold={50}
            increaseViewportBy={200}
            itemContent={(_, order) => (
              <Box
                key={order.id}
                ref={(el) => (orderRefs.current[order.id] = el)}
                className={`p-2 border-b hover:bg-gray-100 hover:cursor-pointer ${
                  order.id === highlightedOrderId
                    ? "bg-blue-100 hover:bg-blue-200"
                    : ""
                }`}
                onClick={() => {
                  handleSelect(order);
                }}
              >
                <DataList.Root size="1" className="gap-2">
                  <DataList.Item>
                    <DataList.Label>Order ID</DataList.Label>
                    <DataList.Value>
                      <CopyableText text={order.id}>
                        {displayUUID(order.id)}
                      </CopyableText>
                    </DataList.Value>
                  </DataList.Item>
                  <DataList.Item>
                    <DataList.Label>Order creation time</DataList.Label>
                    <DataList.Value>
                      {order.time && (
                        <CopyableText text={new Date(order.time).toISOString()}>
                          {formatTimestamp(order.time)}
                        </CopyableText>
                      )}
                    </DataList.Value>
                  </DataList.Item>
                  <DataList.Item>
                    <DataList.Label>Current Assignee</DataList.Label>
                    <DataList.Value>
                      {order.annotator_id ? (
                        <CopyableText text={order.annotator_id}>
                          {annotatorLookup[order.annotator_id] ||
                            displayUUID(order.annotator_id)}
                        </CopyableText>
                      ) : (
                        "-"
                      )}
                    </DataList.Value>
                  </DataList.Item>
                  <DataList.Item>
                    <DataList.Label>Source</DataList.Label>
                    <DataList.Value>{order.order_source || "-"}</DataList.Value>
                  </DataList.Item>
                </DataList.Root>
                <Box mt="1">
                  {order.stage_detail_statuses?.map((status) => (
                    <StageStatusItem
                      key={status.stage}
                      label={status.stage}
                      value={status.status}
                    />
                  ))}
                </Box>
                <Button
                  variant="soft"
                  color="gray"
                  size="2"
                  mt="2"
                  className="w-full"
                  highContrast
                  onClick={(e) => {
                    e.stopPropagation();
                    setStageDetailsOrder(order);
                  }}
                >
                  Show Annotation Stage Details
                </Button>
              </Box>
            )}
            components={{
              Footer,
            }}
          />
        )}
      </div>

      <AnnotationStageDetailsDialog
        order={stageDetailsOrder}
        open={!!stageDetailsOrder}
        onOpenChange={() => setStageDetailsOrder(null)}
      />
    </Box>
  );
}
